import React, { useState, useEffect } from "react";

export default function App() {
  const [score, setScore] = useState(0);
  const [leaderboards, setLeaderboards] = useState(() => {
    const saved = localStorage.getItem("whackLeaderboards");
    return saved ? JSON.parse(saved) : { easy: [], medium: [], hard: [] };
  });
  const [timeLeft, setTimeLeft] = useState(30);
  const [moleIndex, setMoleIndex] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [difficulty, setDifficulty] = useState("medium");
  const [playerName, setPlayerName] = useState("");

  const difficultySpeeds = {
    easy: 1200,
    medium: 800,
    hard: 500,
  };

  // Timer countdown
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (timeLeft <= 0) {
      setGameOver(true);
      updateLeaderboard(score, playerName.trim() || "Player");
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((t) => t - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, gameStarted, gameOver]);

  // Mole pops up depending on difficulty
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const interval = setInterval(() => {
      setMoleIndex(Math.floor(Math.random() * 9));
    }, difficultySpeeds[difficulty]);

    return () => clearInterval(interval);
  }, [gameStarted, gameOver, difficulty]);

  const handleClick = (index) => {
    if (index === moleIndex) {
      setScore((s) => s + 1);
      setMoleIndex(null);
    }
  };

  const startGame = () => {
    if (!playerName.trim()) {
      alert("Please enter your name before starting!");
      return;
    }
    setScore(0);
    setTimeLeft(30);
    setGameOver(false);
    setGameStarted(true);
    setMoleIndex(null);
  };

  const restartGame = () => {
    setScore(0);
    setTimeLeft(30);
    setGameOver(false);
    setGameStarted(false);
    setMoleIndex(null);
    setPlayerName("");
  };

  // Update leaderboard for current difficulty
  const updateLeaderboard = (newScore, name) => {
    const updated = [...leaderboards[difficulty], { name, score: newScore }]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
    const newLeaderboards = { ...leaderboards, [difficulty]: updated };
    setLeaderboards(newLeaderboards);
    localStorage.setItem("whackLeaderboards", JSON.stringify(newLeaderboards));
  };

  // Reset leaderboard for all difficulties
  const resetLeaderboards = () => {
    const empty = { easy: [], medium: [], hard: [] };
    setLeaderboards(empty);
    localStorage.setItem("whackLeaderboards", JSON.stringify(empty));
  };

  return (
    <div style={{ display: "flex", fontFamily: "sans-serif", height: "100vh" }}>
      {/* Left side: Game Grid */}
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#222",
        }}
      >
        {gameStarted && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 100px)",
              gridGap: "10px",
            }}
          >
            {Array.from({ length: 9 }).map((_, index) => (
              <div
                key={index}
                onClick={() => handleClick(index)}
                style={{
                  width: 100,
                  height: 100,
                  backgroundColor: "#444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: 40,
                  borderRadius: "10px",
                  userSelect: "none",
                }}
              >
                {moleIndex === index ? "🐹" : ""}
              </div>
            ))}
          </div>
        )}
        {!gameStarted && <h2 style={{ color: "#fff" }}>Enter name & select difficulty to start</h2>}
      </div>

      {/* Right side: Info & Controls */}
      <div
        style={{
          width: 300,
          padding: 20,
          backgroundColor: "#f0f0f0",
          overflowY: "auto",
        }}
      >
        <h1 style={{ fontSize: "24px" }}>Whack-a-Mole 🐹</h1>
        <h2>Score: {score}</h2>
        <h2>Time Left: {timeLeft}s</h2>

        {/* Player Name */}
        {!gameStarted && (
          <div style={{ marginBottom: 10 }}>
            <label>
              Name:{" "}
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                style={{ padding: "5px", fontSize: "16px", width: "100%" }}
              />
            </label>
          </div>
        )}

        {/* Difficulty & Start */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ marginRight: 10 }}>Difficulty: </label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            disabled={gameStarted}
            style={{ padding: "5px", fontSize: "16px" }}
          >
            <option value="easy">Easy 🐢</option>
            <option value="medium">Medium 🐇</option>
            <option value="hard">Hard 🦅</option>
          </select>
          {!gameStarted && (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={startGame}
                style={{ padding: "10px 20px", fontSize: "16px", cursor: "pointer" }}
              >
                Start Game
              </button>
            </div>
          )}
        </div>

        {/* Game Over & Restart */}
        {gameOver && (
          <div style={{ marginBottom: 20 }}>
            <h2>Game Over!</h2>
            <button onClick={restartGame} style={{ padding: "10px 20px", fontSize: "16px" }}>
              Restart
            </button>
          </div>
        )}

        {/* Leaderboards */}
        <div>
          <h2>🏆 Leaderboards</h2>
          {["easy", "medium", "hard"].map((level) => (
            <div key={level} style={{ marginBottom: 10 }}>
              <h3>{level.charAt(0).toUpperCase() + level.slice(1)}</h3>
              <ol style={{ textAlign: "left" }}>
                {leaderboards[level].length === 0 ? (
                  <p>No scores yet</p>
                ) : (
                  leaderboards[level].map((entry, i) => (
                    <li key={i}>
                      {i === 0 ? "⭐ " : ""}
                      {entry.name}: {entry.score} points
                    </li>
                  ))
                )}
              </ol>
            </div>
          ))}
          <button
            onClick={resetLeaderboards}
            style={{ marginTop: 10, padding: "5px 15px", fontSize: "14px", cursor: "pointer" }}
          >
            Reset All Leaderboards
          </button>
        </div>
      </div>
    </div>
  );
}
